import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { KeyValue } from "./personalDetails";
import { toINR } from "../../utils/utilityFunctions";
import { formatShortDate } from "../../utils/date";
import { RIA_FEE_DECLARATION_POINTS } from "../../utils/constant";

interface Slab {
    rate: string;
    minAum: string;
    maxAum: string;
}

interface RiaFee {
    agreementDate: string;
    feeType: string;
    applicationId: number;
    fixedFee: string;
    variableFee: string;
    slabs: Slab[];
    riaFeeDeclaration: string;
}

const FEE_TYPE_MAP = {
    FIXED: "Fixed fee",
    VARIABLE: "AUA Fee (Variable)",
    SLAB: "AUA Fee (Slab Wise)"
}

export default function RiaFeeDetails({
    application
}: {
    application: any
}): JSX.Element {
    const theme = useTheme();
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const {
        RiaFees = []
    }: { RiaFees: RiaFee[] } = application || {};

    return (
        <Box
            sx={{
                bgcolor: theme.palette.customColors.infoCard,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '10px',
                border: `1px solid ${theme.palette.grey[100]}`,
                padding: '25px',
            }}
            mt={5}
        >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.palette.primary[500], mb: '25px' }}>
                RIA Fee
            </Typography>
            {Array.isArray(RiaFees) && RiaFees.map((fee: RiaFee, index: number) => {
                const declarationPoint = RIA_FEE_DECLARATION_POINTS.find(
                    (point) => point.id === fee.riaFeeDeclaration
                );
                const subPoint = !declarationPoint
                    ? RIA_FEE_DECLARATION_POINTS.flatMap((point) => point.subPoints || []).find(
                        (sp) => sp.id === fee.riaFeeDeclaration
                    )
                    : undefined;
                const declarationText = declarationPoint?.text || subPoint?.text;

                return (
                    <Grid
                        key={index}
                        container
                        rowSpacing={3}
                        columnSpacing={5.5}
                        direction="row"
                        sx={{ mb: 3 }}
                    >
                        {declarationText && (
                            <Grid item xs={12}>
                                <Typography variant="body_500" sx={{ color: theme.palette.text.primary }}>
                                    {declarationText}
                                </Typography>
                            </Grid>
                        )}
                        {fee.feeType === "FIXED" && (
                            <>
                                <KeyValue title="Fee Type" description={FEE_TYPE_MAP[fee.feeType]} />
                                <KeyValue
                                    title="Effective Agreement Date"
                                    description={formatShortDate(fee.agreementDate)}
                                />
                                <KeyValue
                                    title="Annual Fixed Fee Amount (₹)"
                                    description={toINR({ value: fee.fixedFee, locale, currencyCode })}
                                />
                            </>
                        )}
                        {fee.feeType === "VARIABLE" && (
                            <>
                                <KeyValue title="Fee Type" description={FEE_TYPE_MAP[fee.feeType]} />
                                <KeyValue
                                    title="Effective Agreement Date"
                                    description={formatShortDate(fee.agreementDate)}
                                />
                                <KeyValue
                                    title="Fee percentage P.A (%)"
                                    description={fee.variableFee}
                                />
                            </>
                        )}
                        {fee.feeType === "SLAB" && (
                            <>
                                <KeyValue title="Fee Type" description={FEE_TYPE_MAP[fee.feeType]} />
                                <KeyValue
                                    title="Effective Agreement Date"
                                    description={formatShortDate(fee.agreementDate)}
                                />
                                {Array.isArray(fee.slabs) && fee.slabs.map((slab: Slab, slabIndex: number) => (
                                    <Grid
                                        key={slabIndex}
                                        item
                                        container
                                        rowSpacing={3}
                                        columnSpacing={5.5}
                                        direction="row"
                                    >
                                        <Grid item xs={12}>
                                            <Divider sx={{ borderColor: theme.palette.grey[500] }} />
                                        </Grid>
                                        <KeyValue
                                            title="Minimum AUM (₹)"
                                            description={toINR({ value: slab.minAum, locale, currencyCode })}
                                        />
                                        <KeyValue
                                            title="Maximum AUM (₹)"
                                            description={toINR({ value: slab.maxAum, locale, currencyCode })}
                                        />
                                        <KeyValue
                                            title="Fee percentage P.A (%)"
                                            description={slab.rate}
                                        />
                                    </Grid>
                                ))}
                            </>
                        )}
                    </Grid>
                );
            })}
        </Box>
    );
}
